import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import logo from '../assets/logo.png';
import Container from './shared/Container';

const PRODUCTS = [
  { label: 'Vermillion Evident',  sub: 'Any decision guideline, made executable', href: '/products/evident' },
  { label: 'Vermillion Intake', sub: 'Classify, extract, and verify documents at scale', href: '/products/intake' },
];

const NAV_LINKS = [
  { label: 'Benefits', href: '/#benefits' },
  { label: 'Process',  href: '/#process' },
  { label: 'FAQ',      href: '/#faq' },
];

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductsOpen, setIsMobileProductsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled && !location.hash;

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  const navLinkClass = `text-sm font-medium tracking-[0.01em] transition-colors duration-300 ${
    isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-vermillion-600'
  }`;

  return (
    <header className={`fixed top-0 z-40 w-full transition-[box-shadow,background-color,border-color] duration-300 ${
      isTransparent
        ? 'bg-black/20 backdrop-blur-md border-b border-white/[0.08]'
        : 'bg-white/[0.97] shadow-[0_1px_12px_0_rgba(0,0,0,0.06)] border-b border-gray-200/80 backdrop-blur-md'
    }`}>
      <Container>
        <div className="flex h-[4.25rem] items-center justify-between">

          {/* Logo */}
          <a href="/" className="flex items-center">
            <img src={logo} alt="Vermillion" className={`h-8 w-auto transition-all duration-300 ${isTransparent ? 'brightness-0 invert' : ''}`} />
            <span className={`ml-2 text-xl font-semibold transition-colors duration-300 ${isTransparent ? 'text-white' : 'text-gray-900'}`}>
              Vermillion
            </span>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">

            {/* Solutions dropdown */}
            <div
              className="relative"
              ref={dropdownRef}
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              <button
                className={`flex items-center gap-1 text-sm font-medium tracking-[0.01em] transition-colors duration-300 ${
                  isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-600 hover:text-vermillion-600'
                }`}
              >
                Solutions
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-72 pt-3">
                  <div className="rounded-xl border border-gray-100 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.10)] overflow-hidden">
                    <div className="p-1.5">
                      {PRODUCTS.map(p => (
                        <Link
                          key={p.href}
                          to={p.href}
                          onClick={() => setIsDropdownOpen(false)}
                          className="flex flex-col px-3 py-2.5 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-sm font-semibold text-gray-900">{p.label}</span>
                          <span className="text-xs text-gray-400 mt-0.5 leading-snug">{p.sub}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Other links */}
            {NAV_LINKS.map(({ label, href }) => (
              <a key={href} href={href} className={navLinkClass}>{label}</a>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <Link
              to="/request-demo"
              className="inline-flex items-center justify-center py-2 px-[1.125rem] text-sm font-semibold rounded-lg text-white bg-vermillion-600 border border-transparent transition-colors hover:bg-vermillion-700"
            >
              Request Demo
            </Link>
            <button
              className={`inline-flex items-center justify-center p-2 rounded-md transition-colors md:hidden ${
                isTransparent ? 'text-white/80 hover:text-white' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>

        </div>
      </Container>

      {/* Mobile menu */}
      <div className={`md:hidden bg-white border-t border-gray-100${isMobileMenuOpen ? '' : ' hidden'}`}>
        <div className="pt-2 pb-3 px-2 sm:px-3 flex flex-col gap-1">

          {/* Solutions accordion */}
          <button
            className="flex items-center justify-between w-full py-2 px-3 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
            onClick={() => setIsMobileProductsOpen(o => !o)}
          >
            Solutions
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isMobileProductsOpen ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isMobileProductsOpen && (
            <div className="ml-3 flex flex-col gap-0.5">
              {PRODUCTS.map(p => (
                <Link
                  key={p.href}
                  to={p.href}
                  className="block py-2 px-3 rounded-md text-sm font-semibold text-gray-800 hover:bg-gray-50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {p.label}
                </Link>
              ))}
            </div>
          )}

          {NAV_LINKS.map(({ label, href }) => (
            <a
              key={href}
              href={href}
              className="block py-2 px-3 rounded-md text-base font-medium text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {label}
            </a>
          ))}
        </div>
      </div>
    </header>
  );
};

export default Header;
